import { useCallback, useEffect, useState } from "react";
import { useLocation, Redirect } from "wouter";
import type { ViewMode } from "./shared/types";
import { VIEW_PATH, pathToViewMode } from "./shared/types/view-routes";
import { getUserRole } from "./lib/api/client";
import { Sidebar } from "./ui/molecules/Sidebar";
import { UndoFloatingButton } from "./ui/atoms/UndoFloatingButton";
import { BottomNav } from "./ui/molecules/BottomNav";
import { DesktopBottomBar } from "./ui/molecules/DesktopBottomBar";
import { AppModals } from "./ui/molecules/AppModals";
import { AppHeader } from "./features/layout/components/AppHeader";
import { AppContent } from "./features/layout/components/AppContent";
import { SettingsPanel } from "./features/settings/components/SettingsPanel";
import { TimerSettingsPanel } from "./features/settings/components/TimerSettingsPanel";
import { useAppState } from "./hooks/useAppState";
import { useAppData } from "./hooks/useAppData";
import { useAppTimers } from "./hooks/useAppTimers";
import { useAppFiltering } from "./hooks/useAppFiltering";
import { useAppKeyboard } from "./hooks/useAppKeyboard";
import { useModals } from "./hooks/useModals";
import { ModalsProvider } from "./hooks/ModalsProvider";
import { usePageView } from "./lib/analytics/use-page-view";
import { AppViewRenderer } from "./features/layout/components/AppViewRenderer";
import { FullscreenCalendar } from "./ui/molecules/FullscreenCalendar";
import { MobileTimerPanel } from "./ui/molecules/MobileTimerPanel";
import { AchievementsPanel } from "./features/gamification/components/AchievementsPanel";
import { UpdateBanner } from "./ui/atoms/UpdateBanner";
import { NativeUpdateBanner } from "./ui/atoms/NativeUpdateBanner";
import { OfflineBanner } from "./ui/atoms/OfflineBanner";
import { TutorialModal } from "./features/tutorial/components/TutorialModal";
import { DevPanel } from "./ui/molecules/DevPanel";
import { RecoveryKeyReminder } from "./features/auth/components/RecoveryKeyReminder";
import { DesktopTimerPanel } from "./ui/molecules/DesktopTimerPanel";
import { BugReportModal } from "./features/feedback/components/BugReportModal";
import { FullscreenAppPage } from "./features/calendar/components/FullscreenAppPage";

function AppInner() {
  const [location, navigate] = useLocation();
  const modals = useModals();
  const viewMode: ViewMode = pathToViewMode(location);
  const setViewMode = useCallback(
    (mode: ViewMode) => navigate(VIEW_PATH[mode]),
    [navigate],
  );

  const appState = useAppState();
  const appData = useAppData();
  const timers = useAppTimers(appState.showPomodoro, appState.showTwenty, appState.showStopwatch);
  const filtering = useAppFiltering(
    appData.activeTasks,
    appData.events,
    appState.activeFilter,
    appState.hideHidden,
  );

  const isHabitsView = viewMode === "habits";
  const headerCounts = isHabitsView ? {} as Record<import("./shared/types").Category, number> : filtering.counts;
  const headerCompletionRates = isHabitsView ? {} as Record<import("./shared/types").Category, number> : filtering.completionRates;
  const headerTotalCount = isHabitsView ? 0 : filtering.totalCount;

  const [showMobileTimers, setShowMobileTimers] = useState(false);
  const [showDev, setShowDev] = useState(false);
  // Éviter de répéter la vérification du tuto
  const [tutorialChecked, setTutorialChecked] = useState(false);
  const { hasTimers, anyTimerRunning, runningTimerCount } = timers;
  const isAdmin = getUserRole() === "ADMIN";

  usePageView(viewMode);

  useEffect(() => {
    // Ne vérifier qu'une seule fois
    if (tutorialChecked) return;

    // Vérifier d'abord le localStorage (le plus rapide)
    const localCompleted = localStorage.getItem("palnia_tutorial_completed") === "true";
    if (localCompleted) {
      setTutorialChecked(true);
      return;
    }

    // Vérifier côté serveur
    async function checkTutorial() {
      try {
        const res = await fetch("/api/users/me", { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          if (!data.tutorialCompleted) {
            modals.setShowTutorial(true);
          }
        }
      } catch {
        // En cas d'erreur réseau, on suppose que le tuto n'est pas complété
        modals.setShowTutorial(true);
      }
      setTutorialChecked(true);
    }
    checkTutorial();

    // Ouvrir les settings après un link OAuth réussi
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get("oauth");
    if (oauthStatus === "linked" || oauthStatus === "already_linked") {
      modals.setShowSettings(true);
      modals.setSettingsScrollTarget("connected-accounts");
      // Nettoyer l'URL
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete("oauth");
      cleanUrl.searchParams.delete("provider");
      window.history.replaceState({}, "", cleanUrl.pathname);
    }
  }, [tutorialChecked, modals]);

  useAppKeyboard({
    search: appData.search,
    undo: appData.undo,
    redo: appData.redo,
    undoEvent: appData.undoEvent,
    redoEvent: appData.redoEvent,
    focusTask: appData.activeTasks.find((t) => t.status === "doing") ?? null,
    setViewMode,
    setShowFocus: modals.setShowFocus,
    setShowShortcuts: modals.setShowShortcuts,
    viewMode,
  });

  // Swap manifest pour l'installation PWA calendrier fullscreen
  useEffect(() => {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    if (location === "/calendar/fullscreen-app" || location === "/calendar/fullscreen") {
      link.setAttribute("href", "/manifest-fullscreen-calendar.json");
    } else {
      link.setAttribute("href", "/manifest.json");
    }
  }, [location]);

  if (location === "/calendar/fullscreen-app") {
    return <FullscreenAppPage />;
  }

  if (location === "/calendar/fullscreen") {
    return (
      <FullscreenCalendar
        events={filtering.filteredEvents}
        tasks={filtering.filteredTasks}
        activeCategory={filtering.defaultCategory}
        googleCalendarUi={appState.googleCalendarUi}
        readOnly={appState.calendarReadOnly}
        onAddEvent={appData.addEvent}
        onAddEventFull={appData.addEventFull}
        onDeleteEvent={appData.deleteEvent}
        onUpdateEvent={appData.updateEvent}
        onDetachOccurrence={appData.detachOccurrence}
        onAddRecurrenceException={appData.addRecurrenceException}
        onResyncOccurrence={appData.resyncOccurrence}
        onResyncAllOccurrences={appData.resyncAllOccurrences}
        onUndoEvent={appData.undoEvent}
      />
    );
  }

  return (
    <div className="h-dvh overflow-hidden flex flex-col bg-surface md:flex-row max-w-screen">
      <UpdateBanner />
      <NativeUpdateBanner />
      <OfflineBanner />
      <RecoveryKeyReminder />
      <UndoFloatingButton />
      {location === "/" && <Redirect to="/today" />}
      {!appState.advancedMode &&
        (viewMode === "kanban" || viewMode === "eisenhower" || viewMode === "journal") && (
          <Redirect to="/today" />
        )}
      {viewMode !== "today" && appState.hiddenSections?.has(viewMode) && (
        <Redirect to="/today" />
      )}
      {!appState.desktopBottomBar && (
        <Sidebar
          advancedMode={appState.advancedMode}
          hiddenSections={appState.hiddenSections}
          onSettings={() => modals.setShowSettings((v) => !v)}
          onBugReport={() => modals.setShowBugReport(true)}
          onAdmin={isAdmin ? () => modals.setShowAdmin(true) : undefined}
          onDev={import.meta.env.DEV ? () => setShowDev(true) : undefined}
          onPinnedChange={appState.setSidebarPinned}
        />
      )}

      <div
        className={`flex-1 flex flex-col overflow-hidden min-h-0 ${appState.desktopBottomBar ? "" : appState.sidebarPinned ? "md:ml-42" : "md:ml-14"}`}
      >
        <AppHeader
          sidebarPinned={appState.sidebarPinned}
          viewMode={viewMode}
          activeFilter={appState.activeFilter}
          hideHidden={appState.hideHidden}
          isAdmin={isAdmin}
          counts={headerCounts}
          completionRates={headerCompletionRates}
          totalCount={headerTotalCount}
          pomodoroActive={timers.pomodoro.state.isRunning}
          pomodoroTimeLeft={timers.pomodoro.state.timeLeft}
          hasTimers={hasTimers}
          anyTimerRunning={anyTimerRunning}
          runningTimerCount={runningTimerCount}
          onSearch={() => appData.search.open()}
          onSettings={() => modals.setShowSettings(true)}
          onBugReport={() => modals.setShowBugReport(true)}
          onFilterChange={appState.setActiveFilter}
          onHideHiddenToggle={() => appState.setHideHidden((h) => !h)}
          onTimerToggle={() => setShowMobileTimers((v) => !v)}
          desktopBottomBar={appState.desktopBottomBar}
        />

        {modals.showSettings && (
          <SettingsPanel
            theme={appData.theme}
            hasFocusTask={appData.activeTasks.some((t) => t.status === "doing")}
            onThemeChange={appData.setTheme}
            onImport={appData.handleImport}
            notificationPermission={appData.notificationPermission}
            onRequestNotificationPermission={appData.requestNotificationPermission}
          />
        )}

        <DesktopTimerPanel
          showPomodoro={appState.showPomodoro}
          showTwenty={appState.showTwenty}
          showStopwatch={appState.showStopwatch}
          pomodoro={timers.pomodoro}
          twenty={timers.twenty}
          stopwatch={timers.stopwatch}
        />

        {modals.showTimerSettings && (
          <TimerSettingsPanel
            onClose={() => modals.setShowTimerSettings(false)}
          />
        )}

        <AppContent
          viewMode={viewMode}
          sidebarPinned={appState.sidebarPinned}
          filteredTasks={filtering.filteredTasks}
          filteredEvents={filtering.filteredEvents}
          expandedEvents={filtering.expandedEvents}
          visibleEvents={filtering.visibleEvents}
          activeTasks={appData.activeTasks}
          events={appData.events}
          defaultCategory={filtering.defaultCategory}
          showTemplates={modals.showTemplates}
          onSetShowTemplates={modals.setShowTemplates}
          children={AppViewRenderer({
            appData,
            filtering,
            appState,
            setViewMode,
          })}
        />

        {appState.desktopBottomBar && (
          <DesktopBottomBar
            advancedMode={appState.advancedMode}
            hiddenSections={appState.hiddenSections}
            onSearch={() => appData.search.open()}
            onSettings={() => modals.setShowSettings(true)}
            onBugReport={() => modals.setShowBugReport(true)}
            onAdmin={isAdmin ? () => modals.setShowAdmin(true) : undefined}
            onDev={import.meta.env.DEV ? () => setShowDev(true) : undefined}
          />
        )}

        <BottomNav
          advancedMode={appState.advancedMode}
          hiddenSections={appState.hiddenSections}
          onAdmin={isAdmin ? () => modals.setShowAdmin(true) : undefined}
          onDev={import.meta.env.DEV ? () => setShowDev(true) : undefined}
        />
      </div>

      {modals.showAchievements && (
        <AchievementsPanel
          state={appData.gamification.state}
          onClose={() => modals.setShowAchievements(false)}
        />
      )}

      {modals.showTutorial && (
        <TutorialModal
          onClose={() => modals.setShowTutorial(false)}
          onNavigate={navigate}
        />
      )}

      {/* Mobile: timer panel overlay */}
      <MobileTimerPanel
        open={showMobileTimers}
        onClose={() => setShowMobileTimers(false)}
        showPomodoro={appState.showPomodoro}
        showTwenty={appState.showTwenty}
        showStopwatch={appState.showStopwatch}
        pomodoro={timers.pomodoro}
        twenty={timers.twenty}
        stopwatch={timers.stopwatch}
        onTimerSettings={() => {
          setShowMobileTimers(false);
          modals.setShowTimerSettings(true);
        }}
      />

      <AppModals
        search={appData.search}
        focusTask={
          appData.activeTasks.find((t) => t.status === "doing") ?? null
        }
        taskUndoDelete={appData.taskUndoDelete}
        eventUndoDelete={appData.eventUndoDelete}
        onSetViewMode={setViewMode}
        onCycleStatus={appData.cycleStatus}
        onCompleteFocus={() => modals.setShowFocus(false)}
        onUpdateTaskStatus={(id, status) => appData.updateTask(id, { status })}
        pendingRecurrenceDelete={appData.pendingRecurrenceDelete}
        onRecurrenceDeleteChoice={appData.handleRecurrenceDeleteChoice}
        onCancelRecurrenceDelete={() => appData.setPendingRecurrenceDelete(null)}
      />

      {modals.showBugReport && (
        <BugReportModal onClose={() => modals.setShowBugReport(false)} />
      )}

      {import.meta.env.DEV && showDev && (
        <DevPanel onClose={() => setShowDev(false)} />
      )}
    </div>
  );
}

export function App() {
  return (
    <ModalsProvider>
      <AppInner />
    </ModalsProvider>
  );
}
